import { z } from "zod";
import { SUPPORTED_LOCALES } from "../locales.js";
import { CURRENCIES } from "../status.js";
import { isValidIban, normalizeIban } from "../qrbill.js";
import { countryCodeSchema } from "../countries.js";
import {
  dateOnlySchema,
  nullableDate,
  nullableText,
  nonNegativeAmountSchema,
  versionSchema,
} from "./common.js";

const nullableMoney = nonNegativeAmountSchema.nullable().optional();

export function normalizeAhvNumber(value: string): string {
  return value.replace(/\D/g, "");
}

/** Swiss AHV/AVS number: 756 + nine digits + EAN-13 checksum. */
export function isValidAhvNumber(value: string): boolean {
  const digits = normalizeAhvNumber(value);
  if (!/^756\d{10}$/.test(digits)) return false;
  let sum = 0;
  for (let index = 0; index < 12; index += 1) {
    sum += Number(digits[index]) * (index % 2 === 0 ? 1 : 3);
  }
  return (10 - (sum % 10)) % 10 === Number(digits[12]);
}

export const employeeProfileUpdateSchema = z
  .object({
    employeeNumber: nullableText(40),
    phone: nullableText(40),
    addressLine1: nullableText(160),
    addressLine2: nullableText(160),
    postalCode: nullableText(20),
    city: nullableText(100),
    canton: nullableText(2),
    country: countryCodeSchema.optional(),
    dateOfBirth: nullableDate,
    gender: z.enum(["female", "male", "diverse", "unspecified"]).optional(),
    nationality: countryCodeSchema.nullable().optional(),
    ahvNumber: nullableText(40),
    bankName: nullableText(160),
    bankIban: nullableText(40),
    emergencyContactName: nullableText(160),
    emergencyContactPhone: nullableText(40),
    jobTitle: nullableText(120),
    department: nullableText(120),
    employmentStart: nullableDate,
    employmentEnd: nullableDate,
    salaryType: z.enum(["monthly", "hourly"]).optional(),
    salaryAmount: nullableMoney,
    salaryBasis: z.enum(["full_time", "actual_pensum"]).optional(),
    salaryCurrency: z.enum(CURRENCIES).optional(),
    pensumPercent: z
      .union([z.string(), z.number()])
      .pipe(z.coerce.number().min(1).max(100))
      .optional(),
    weeklyHoursAt100: z
      .union([z.string(), z.number()])
      .pipe(z.coerce.number().min(1).max(80))
      .optional(),
    hasThirteenthSalary: z.boolean().optional(),
    vacationWeeksOverride: z
      .union([z.string(), z.number()])
      .pipe(z.coerce.number().min(0).max(20))
      .nullable()
      .optional(),
    /** Internal cost per hour, used to price approved project time. */
    laborCostRate: nullableMoney,
    internalNotes: nullableText(6000),
  })
  .superRefine((value, ctx) => {
    if (value.ahvNumber && !isValidAhvNumber(value.ahvNumber)) {
      ctx.addIssue({
        code: "custom",
        path: ["ahvNumber"],
        message: "Invalid AHV/AVS number",
      });
    }
    if (value.bankIban && !isValidIban(value.bankIban)) {
      ctx.addIssue({
        code: "custom",
        path: ["bankIban"],
        message: "Invalid IBAN checksum",
      });
    }
    if (
      value.employmentStart &&
      value.employmentEnd &&
      value.employmentEnd < value.employmentStart
    ) {
      ctx.addIssue({
        code: "custom",
        path: ["employmentEnd"],
        message: "Employment end must not precede employment start",
      });
    }
  })
  .transform((value) => ({
    ...value,
    ...(value.ahvNumber
      ? { ahvNumber: normalizeAhvNumber(value.ahvNumber) }
      : {}),
    ...(value.bankIban ? { bankIban: normalizeIban(value.bankIban) } : {}),
  }));
export type EmployeeProfileUpdateDto = z.infer<
  typeof employeeProfileUpdateSchema
>;
export const employeeInvitationProfileSchema = z
  .object({
    phone: nullableText(40),
    addressLine1: nullableText(160),
    addressLine2: nullableText(160),
    postalCode: nullableText(20),
    city: nullableText(100),
    country: countryCodeSchema.nullable().optional(),
    dateOfBirth: nullableDate,
    nationality: countryCodeSchema.nullable().optional(),
    ahvNumber: nullableText(40),
    bankName: nullableText(160),
    bankIban: nullableText(40),
    preferredLocale: z.enum(SUPPORTED_LOCALES).optional(),
  })
  .strict()
  .superRefine((value, ctx) => {
    if (value.ahvNumber && !isValidAhvNumber(value.ahvNumber)) {
      ctx.addIssue({
        code: "custom",
        path: ["ahvNumber"],
        message: "Invalid AHV/AVS number",
      });
    }
    if (value.bankIban && !isValidIban(value.bankIban)) {
      ctx.addIssue({
        code: "custom",
        path: ["bankIban"],
        message: "Invalid IBAN checksum",
      });
    }
  })
  .transform((value) => ({
    ...value,
    ...(value.ahvNumber
      ? { ahvNumber: normalizeAhvNumber(value.ahvNumber) }
      : {}),
    ...(value.bankIban ? { bankIban: normalizeIban(value.bankIban) } : {}),
  }));
export type EmployeeInvitationProfileDto = z.infer<
  typeof employeeInvitationProfileSchema
>;
export const employeeSelfServiceProfileSchema = employeeInvitationProfileSchema;
export type EmployeeSelfServiceProfileDto = EmployeeInvitationProfileDto;

/** Management edits use optimistic concurrency. */
export const employeeProfileEditSchema = z.intersection(
  employeeProfileUpdateSchema,
  z.object({ version: versionSchema }),
);

export type EmployeeProfileEditDto = z.infer<typeof employeeProfileEditSchema>;

export const employeeScheduleUpdateSchema = z.object({
  days: z
    .array(
      z.object({
        weekday: z.number().int().min(1).max(7),
        plannedMinutes: z
          .number()
          .int()
          .min(0)
          .max(24 * 60),
      }),
    )
    .length(7)
    .refine(
      (days) => new Set(days.map((day) => day.weekday)).size === 7,
      "Each weekday is required once",
    ),
});
export type EmployeeScheduleUpdateDto = z.infer<
  typeof employeeScheduleUpdateSchema
>;

export const employeeBalanceUpdateSchema = z.object({
  year: z.number().int().min(2000).max(2200),
  vacationCarryoverMinutes: z
    .number()
    .int()
    .min(-100_000)
    .max(100_000)
    .default(0),
  vacationAdjustmentMinutes: z
    .number()
    .int()
    .min(-100_000)
    .max(100_000)
    .default(0),
  compensationAdjustmentMinutes: z
    .number()
    .int()
    .min(-100_000)
    .max(100_000)
    .default(0),
  notes: nullableText(2000),
});
export type EmployeeBalanceUpdateDto = z.infer<
  typeof employeeBalanceUpdateSchema
>;

export const employeeOffboardSchema = z.object({
  employmentEnd: z.union([z.literal("now"), dateOnlySchema]),
  reason: z.string().trim().min(1).max(2000),
});
export type EmployeeOffboardDto = z.infer<typeof employeeOffboardSchema>;

export const employeeDocumentMetadataSchema = z.object({
  title: z.string().trim().min(1).max(180),
  categoryId: z.string().uuid().nullable().optional(),
  visibility: z.enum(["management", "employee"]),
  documentDate: nullableDate,
  year: z.coerce.number().int().min(1900).max(2200).nullable().optional(),
  month: z.coerce.number().int().min(1).max(12).nullable().optional(),
});
export type EmployeeDocumentMetadataDto = z.infer<
  typeof employeeDocumentMetadataSchema
>;

export const employeeDocumentCategorySchema = z.object({
  name: z.string().trim().min(1).max(100),
  active: z.boolean().default(true),
  sortOrder: z.number().int().min(0).max(100_000).default(0),
  translations: z
    .record(z.enum(["de", "fr", "es"]), z.string().trim().min(1).max(100))
    .default({}),
});
export type EmployeeDocumentCategoryDto = z.infer<
  typeof employeeDocumentCategorySchema
>;
